import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaAlignLeft, FaSave, FaTimes, FaMapMarkerAlt } from 'react-icons/fa';

const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 2rem;
  overflow-y: auto;
`;

const ModalContainer = styled(motion.div)`
  background: white;
  border-radius: 20px;
  width: 100%;
  max-width: 580px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  margin: auto;
`;

const FormHeader = styled.div`
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  padding: 2rem;
  color: white;
  text-align: center;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.05);
  }
`;

const FormTitle = styled.h2`
  font-size: 1.625rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
`;

const FormSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.9rem;
  margin: 0;
`;

const FormBody = styled.form`
  padding: 2rem;
`;

const InputGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;

  svg {
    color: #6366f1;
    font-size: 0.875rem;
  }
`;

const InputRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.875rem;
  color: #1f2937;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &::placeholder {
    color: #9ca3af;
  }

  &:hover {
    border-color: #6366f1;
  }

  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.875rem 1rem;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.875rem;
  color: #1f2937;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:hover {
    border-color: #6366f1;
  }

  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.875rem 1rem;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 0.875rem;
  color: #1f2937;
  min-height: 100px;
  resize: vertical;
  transition: all 0.2s ease;
  box-sizing: border-box;
  font-family: inherit;

  &::placeholder {
    color: #9ca3af;
  }

  &:hover {
    border-color: #6366f1;
  }

  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e5e7eb;
`;

const Button = styled.button`
  padding: 0.875rem 1.5rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s ease;
  cursor: pointer;
  min-width: 110px;
  justify-content: center;

  ${props => props.primary && `
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: white;
    border: none;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none;
    }
  `}

  ${props => props.secondary && `
    background: #f3f4f6;
    color: #4b5563;
    border: 2px solid #e5e7eb;

    &:hover {
      background: #e5e7eb;
      color: #1f2937;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  `}

  svg {
    font-size: 0.875rem;
  }
`;

const ErrorAlert = styled.div`
  background: #fef2f2;
  border: 1px solid #fed7d7;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1.5rem;
  color: #b91c1c;
  font-size: 0.875rem;
  font-weight: 500;
`;

const EventForm = ({ event, onSubmit, onCancel, branches = [] }) => {
  const [formData, setFormData] = useState({
    summary: event?.summary || '',
    description: event?.description || '',
    start: event?.start?.dateTime || '',
    end: event?.end?.dateTime || '',
    location: event?.location || '',
    type: event?.type || 'special',
    branch: event?.branch || 'all',
    attachments: event?.attachments || [],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    
    try {
      // Convert start/end to { dateTime: ... }
      const payload = {
        ...formData,
        start: { dateTime: formData.start },
        end: { dateTime: formData.end },
      };
      
      await onSubmit(payload);
      // If we get here, submission was successful
    } catch (err) {
      console.error('Form submission error:', err);
      setError(err.message || 'Failed to save event');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={handleOverlayClick}
    >
      <ModalContainer
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        transition={{ duration: 0.2 }}
      >
        <FormHeader>
          <CloseButton onClick={onCancel}>
            <FaTimes />
          </CloseButton>
          <FormTitle>{event ? 'Edit Event' : 'Create New Event'}</FormTitle>
          <FormSubtitle>
            {event ? 'Update the event details below' : 'Fill in the details to create a new event'}
          </FormSubtitle>
        </FormHeader>

        <FormBody onSubmit={handleSubmit}>
          {error && (
            <ErrorAlert>
              {error}
            </ErrorAlert>
          )}
          
          <InputRow>
            <InputGroup>
              <Label><FaAlignLeft /> Summary</Label>
              <Input 
                name="summary" 
                value={formData.summary} 
                onChange={handleChange} 
                placeholder="Event summary" 
                required 
                disabled={isSubmitting}
              />
            </InputGroup>
            
            <InputGroup>
              <Label><FaMapMarkerAlt /> Location</Label>
              <Input 
                name="location" 
                value={formData.location} 
                onChange={handleChange} 
                placeholder="Location" 
                required 
                disabled={isSubmitting}
              />
            </InputGroup>
          </InputRow>
          
          <InputRow>
            <InputGroup>
              <Label><FaCalendarAlt /> Start Date & Time</Label>
              <Input 
                type="datetime-local" 
                name="start" 
                value={formData.start} 
                onChange={handleChange} 
                required 
                disabled={isSubmitting}
              />
            </InputGroup>
            
            <InputGroup>
              <Label><FaCalendarAlt /> End Date & Time</Label>
              <Input 
                type="datetime-local" 
                name="end" 
                value={formData.end} 
                onChange={handleChange} 
                required 
                disabled={isSubmitting}
              />
            </InputGroup>
          </InputRow>
          
          <InputRow>
            <InputGroup>
              <Label>Type</Label>
              <Select name="type" value={formData.type} onChange={handleChange} required disabled={isSubmitting}>
                <option value="special">Special</option>
                <option value="regular">Regular</option>
                <option value="outreach">Outreach</option>
              </Select>
            </InputGroup>
            
            <InputGroup>
              <Label>Branch</Label>
              <Select name="branch" value={formData.branch} onChange={handleChange} required disabled={isSubmitting}>
                <option value="all">All Branches</option>
                {branches.map(branch => {
                  return (
                    <option key={branch._id || branch.name} value={branch._id}>
                      {branch.name}
                    </option>
                  );
                })}
              </Select>
            </InputGroup>
          </InputRow>
          
          <InputGroup>
            <Label>Description</Label>
            <TextArea 
              name="description" 
              value={formData.description} 
              onChange={handleChange} 
              placeholder="Event description" 
              required 
              disabled={isSubmitting}
            />
          </InputGroup>
          
          <ButtonGroup>
            <Button type="button" secondary onClick={onCancel} disabled={isSubmitting}>
              <FaTimes /> Cancel
            </Button>
            <Button type="submit" primary disabled={isSubmitting}>
              <FaSave /> {isSubmitting ? 'Saving...' : 'Save Event'}
            </Button>
          </ButtonGroup>
        </FormBody>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default EventForm; 